import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import DataPageLayout from '../components/DataPageLayout';
import ImportPreviewModal from '../components/ImportPreviewModal';
import { verifyImportPorts, addPort, importPorts, listPorts, updatePort, deletePorts } from '../services/portService';
import './Dashboard.css';

const MAX_IMPORT_FILE_SIZE = 10 * 1024 * 1024;

function formatCount(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  return Number(value).toLocaleString();
}

function formatDate(value) {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString();
}

function Ports() {
  const fileInputRef = useRef(null);
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [error, setError] = useState('');
  const [importMessage, setImportMessage] = useState('');
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmingImport, setConfirmingImport] = useState(false);

  const loadPorts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPorts();
      setPorts(data);
      setError('');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
      setPorts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPorts();
  }, [loadPorts]);

  const uniqueCities = useMemo(
    () => new Set(ports.map((port) => port.city)).size,
    [ports]
  );

  const metrics = [
    {
      title: 'Total Ports',
      value: loading ? '...' : formatCount(ports.length),
      subtext: 'Ports in your list',
      highlight: true,
    },
    {
      title: 'Unique Cities',
      value: loading ? '...' : formatCount(uniqueCities),
      subtext: 'Different cities',
    },
    {
      title: 'Reserved',
      value: '—',
      subtext: 'Coming soon',
    },
    {
      title: 'Reserved',
      value: '—',
      subtext: 'Coming soon',
    },
  ];

  const handleImportClick = () => {
    setImportMessage('');
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only .csv files are supported.');
      return;
    }

    if (file.size > MAX_IMPORT_FILE_SIZE) {
      toast.error('File is too large. Maximum size is 10MB.');
      return;
    }

    setImportLoading(true);
    setImportMessage('');
    setError('');
    setPreviewLoading(true);
    setPendingImportFile(file);
    setImportPreview({ rows: [], total: 0, new: 0, exists: 0, invalid: 0 });

    try {
      const preview = await verifyImportPorts(file);
      setImportPreview(preview);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
      setPendingImportFile(null);
      setImportPreview(null);
    } finally {
      setImportLoading(false);
      setPreviewLoading(false);
    }
  };

  const handleCloseImportPreview = () => {
    if (confirmingImport) {
      return;
    }

    setPendingImportFile(null);
    setImportPreview(null);
    setPreviewLoading(false);
  };

  const handleConfirmImport = async () => {
    if (!pendingImportFile) {
      return;
    }

    setConfirmingImport(true);
    setError('');

    try {
      const result = await importPorts(pendingImportFile);
      const message = `Imported ${result.inserted} port(s).`;
      setImportMessage(message);
      setPendingImportFile(null);
      setImportPreview(null);
      await loadPorts();
      toast.success(message);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setConfirmingImport(false);
    }
  };

  const importPreviewColumns = [
    { key: 'name', label: 'Port Name' },
    { key: 'city', label: 'City' },
  ];

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Port Name', className: 'data-page__cell-truncate' },
    { key: 'city', label: 'City' },
    {
      key: 'created_at',
      label: 'Added At',
      render: (row) => formatDate(row.created_at),
    },
  ];

  const portFields = [
    { key: 'name', label: 'Port Name' },
    { key: 'city', label: 'City' },
  ];

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleImportFile}
        hidden
      />
      <DataPageLayout
        title="Ports"
        subtitle="Ports and the cities they belong to."
        metrics={metrics}
        loading={loading}
        error={error}
        successMessage={importMessage}
        onRefresh={loadPorts}
        importLabel="Import Ports"
        onImportClick={handleImportClick}
        importLoading={importLoading}
        addLabel="Add Port"
        addFields={portFields}
        onSaveAdd={async (values) => {
          try {
            await addPort({
              name: values.name.trim(),
              city: values.city.trim(),
            });
            setError('');
            setImportMessage('Port added successfully.');
            await loadPorts();
            toast.success('Port added successfully.');
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        columns={columns}
        rows={ports}
        emptyMessage="No ports found."
        editFields={portFields}
        onSaveEdit={async (row, values) => {
          try {
            await updatePort(row.id, {
              name: values.name.trim(),
              city: values.city.trim(),
            });
            await loadPorts();
            toast.success('Port updated successfully.');
          } catch (err) {
            toast.error(err.message);
            throw err;
          }
        }}
        onDelete={async (row) => {
          try {
            await deletePorts([row.id]);
            await loadPorts();
            setError('');
            toast.success('Port deleted successfully.');
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        onDeleteMany={async (selected) => {
          try {
            await deletePorts(selected.map((row) => row.id));
            await loadPorts();
            setError('');
            toast.success(`Deleted ${selected.length} port(s).`);
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        onDeleteAll={async (allRows) => {
          try {
            await deletePorts(allRows.map((row) => row.id));
            await loadPorts();
            setError('');
            toast.success(`Deleted all ${allRows.length} port(s).`);
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        deleteConfirmMessage="Delete this port?"
        deleteManyConfirmMessage="Delete selected ports?"
        deleteAllConfirmMessage="Delete all ports?"
      />

      {importPreview && (
        <ImportPreviewModal
          title="Import Ports Preview"
          fileName={pendingImportFile?.name}
          columns={importPreviewColumns}
          rows={importPreview.rows}
          total={importPreview.total}
          newCount={importPreview.new}
          existsCount={importPreview.exists}
          invalidCount={importPreview.invalid}
          loading={previewLoading}
          confirming={confirmingImport}
          onClose={handleCloseImportPreview}
          onConfirm={handleConfirmImport}
        />
      )}
    </>
  );
}

export default Ports;
